<template>
  <v-card class="import-section">
    <v-card-title class="justify-center">
      {{ $t("contract.contract_percentage_import_title") }}
    </v-card-title>
    <v-card-text class="d-flex flex-column">
      <v-form ref="form">
        <v-row no-gutters>
          <v-col cols="3" class="input-label"
            >{{ $t("contract.contract_percentage_year") }}
          </v-col>
          <v-col cols="9">
            <v-select
              outlined
              dense
              :items="contractYearOptions"
              v-model="contractYear"
              :rules="requiredRules"
            ></v-select>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="3" class="input-label"
            >{{ $t("contract.contract_percentage_excel") }} (Optional)
          </v-col>
          <v-col cols="9">
            <FileInput
              v-model="excelFile"
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            ></FileInput>
          </v-col>
        </v-row>

        <v-row no-gutters style="margin-top: 1.25rem">
          <v-col cols="3" class="input-label"
            >Copy from Contract (Optional)
          </v-col>
          <v-col cols="9">
            <v-select
              outlined
              dense
              clearable
              :items="contractOptions"
              v-model="cloneFromContract"
            ></v-select>
          </v-col>
        </v-row>

        <v-row v-if="cloneFromContract" no-gutters style="margin-top: 1.25rem">
          <v-col cols="3" class="input-label">Copy from Contract Year </v-col>
          <v-col cols="9">
            <v-select
              outlined
              dense
              clearable
              :items="existsContractYearOptions"
              v-model="cloneFromContractYear"
            ></v-select>
          </v-col>
        </v-row>

        <v-row no-gutters v-if="cloneFromContract && haveSpecialPermission">
          <v-col style="justify-content: center; color: red">
            After copying from Contract Year, Please modify the base start and
            end in the configurations.
          </v-col>
        </v-row>

        <v-row no-gutters v-if="!cloneFromContract && haveSpecialPermission">
          <v-col cols="3" class="input-label">Base Start </v-col>
          <v-col cols="9" class="input-label">
            <RbuPeriodInput
              v-model="base_start"
              :rules="requiredRules"
              :min="min"
              :max="max"
            ></RbuPeriodInput>
          </v-col>
        </v-row>

        <v-row no-gutters v-if="!cloneFromContract && haveSpecialPermission">
          <v-col cols="3" class="input-label">Base End </v-col>
          <v-col cols="9" class="input-label">
            <RbuPeriodInput
              v-model="base_end"
              :rules="requiredRules"
              :min="min"
              :max="max"
            ></RbuPeriodInput>
          </v-col>
        </v-row>

        <v-row no-gutters v-if="!cloneFromContract && haveSpecialPermission">
          <v-col cols="3" class="input-label">Contract Percentage </v-col>
          <v-col cols="9" class="input-label">
            <v-text-field
              outlined
              type="number"
              min="0"
              step=".01"
              suffix="%"
              v-model="contractPercentage"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn color="secondary" @click="$emit('submit', false)" depressed large>
        {{ $t("cancel") }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        @click="$refs.form.validate() && confirm()"
        depressed
        large
      >
        {{ $t("save") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { onMounted, ref, computed, watch } from "@vue/composition-api";
import { ACTION_TYPES } from "@/store/types";
import FileInput from "@/components/FileInput";
import RbuPeriodInput from "@/components/rbu/RbuPeriodInput.vue";
import { SPECIAL_PERMISSIONS } from "@/constants";

export default {
  name: "ImportContractPercentageDialog",
  components: { FileInput, RbuPeriodInput },
  props: {
    contractId: String,
  },
  setup(props, { root, emit }) {
    const requiredRules = ref([
      (value) => !!value || root.$t("rules.required"),
    ]);
    const min = ref(undefined);
    const max = ref(undefined);

    const contractOptions = ref([]);
    const cloneFromContract = ref(undefined);

    const contractYearOptions = ref(["A", "B", "C", "D", "E"]);
    const contractYear = ref(null);
    const excelFile = ref(null);
    const contractPercentage = ref(0);
    const base_start = ref("202401");
    const base_end = ref("202401");

    const cloneFromContractYear = ref(undefined);
    const existsContractYear = ref([]);
    const existsContractYearOptions = computed(() => {
      if (existsContractYear.value) {
        return existsContractYear.value.map((y) => y.year);
      }

      return [];
    });

    const haveSpecialPermission = computed(() => {
      return root.$store.getters.hasSpecialPermission(
        SPECIAL_PERMISSIONS.CONTRACT_PERCENTAGE
      );
    });

    const confirm = async function () {
      let formData = new FormData();
      formData.append("contract_id", props.contractId);
      formData.append("contract_year", contractYear.value);
      formData.append("contractPercentage", contractPercentage.value || 0);
      formData.append("base_start", base_start.value);
      formData.append("base_end", base_end.value);
      if (cloneFromContract.value) {
        formData.append("cloneContract", cloneFromContract.value);
        if (cloneFromContractYear.value) {
          formData.append("cloneYear", cloneFromContractYear.value);
        } else {
          alert("Please Pick the contract Year");
          return;
        }
      }
      if (excelFile.value) {
        formData.append("excel", excelFile.value, excelFile.value.name);
      }

      let response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: "admin/contract/contract_percentage/import/",
        params: formData,
      });

      if (response.body.success) {
        emit("submit", true);
      }
    };

    const getContract = async function () {
      const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: "admin/contract/list_ignore_permissions/",
        params: { page_size: -1 },
      });

      if (response && response.data && response.data.success) {
        contractOptions.value = (response.data.contracts || [])
          .sort((a, b) => a.id.localeCompare(b.id)) // Sort contracts by id
          .map((c) => {
            return { text: c.id, value: c.id };
          });
      }
    };

    const getContractYear = async function () {
      const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: "admin/contract/year/",
        params: {
          contract_id: cloneFromContract.value,
        },
      });

      if (response && response.data && response.data.success) {
        existsContractYear.value = response.data.years || [];
      }
    };

    const getAvailablePeriod = async function () {
      const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: "admin/dataset/available_data_period/",
        params: {},
      });

      if (response && response.data && response.data.success) {
        min.value = response.data.min;
        max.value = response.data.max;
      }
    };

    onMounted(() => {
      // getContractYear();
      getAvailablePeriod();
      getContract();
    });

    watch(cloneFromContract, (newValue, _) => {
      if (newValue) {
        cloneFromContractYear.value = undefined;
        getContractYear();
      }
    });

    return {
      requiredRules,

      contractYearOptions,
      contractYear,
      excelFile,

      confirm,
      contractPercentage,
      base_end,
      base_start,
      existsContractYearOptions,
      cloneFromContractYear,
      min,
      max,
      contractOptions,
      cloneFromContract,
      haveSpecialPermission,
    };
  },
};
</script>

<style lang="less">
.import-section {
  .input-label {
    text-align: right;
    padding: 4px 16px 0 0 !important;
  }

  .step-1-wrapper {
    flex: 1 0 0;
    display: flex;
    justify-content: space-evenly;
    align-items: center;
  }
}
</style>
